import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import queryString from "qs";
import { instance } from "api/apiBase";
import { LeadsFilters } from "shared/app/LeadsTableView/types";
import type { PrescreeningResult } from "api/lead";

interface LeadsFilterOptions {
  statuses?: { status: string; count: number }[];
  properties?: { id: number; name: string; count: number }[];
  units?: { id: number; title: string; property_id: number; count: number }[];
  lead_sources?: {
    id: number;
    name: string;
    source_type: string;
    display_order: number;
    count: number;
  }[];
  /** Every tag in the account A to Z, including tags no matching lead carries. */
  tags?: { id: number; name: string; count: number }[];
  lead_types?: { lead_type: string; count: number }[];
  stages?: { id: number; name: string; count: number }[];
  activity_actor_types?: { actor_type: string; count: number }[];
  activity_types?: { activity_type: string; count: number }[];
  prescreening_results?: { result: PrescreeningResult; count: number }[];
  actors?: { type: string; id: number | null; name: string; count: number }[];
  needs_follow_up?: number;
}

export const LEADS_FILTER_OPTIONS_QUERY_KEY = "LEADS_FILTER_OPTIONS";

const QUERY = "/portal/v1/leads/filter_options";

const toNumberIds = (items?: { id: string }[]) =>
  items?.length ? items.map((item) => Number(item.id)) : undefined;

/**
 * Per-value lead counts for the filter controls, counted over the applied filters
 * (each facet ignores its own dimension). Shared by the Filters drawer and the Tags
 * filter so both read one cached request.
 */
export const useLeadsFilterOptions = ({
  filters,
  propertyIds,
  unitIds,
  search,
  enabled = true
}: {
  filters?: LeadsFilters;
  propertyIds?: string[];
  unitIds?: string[];
  search?: string;
  enabled?: boolean;
}) => {
  const params = useMemo(
    () => ({
      property_ids: propertyIds || toNumberIds(filters?.properties),
      unit_ids: unitIds,
      stage_ids: toNumberIds(filters?.stages),
      source_ids: toNumberIds(filters?.leadSources),
      tag_ids: toNumberIds(filters?.tags),
      most_recent_activity: filters?.activityTypes?.length
        ? { type: filters.activityTypes }
        : undefined,
      exclude_invite_sources:
        filters?.showBoomScreenSources === false || undefined,
      search: search?.trim() || undefined
    }),
    [
      propertyIds,
      unitIds,
      filters?.properties,
      filters?.stages,
      filters?.leadSources,
      filters?.tags,
      filters?.activityTypes,
      filters?.showBoomScreenSources,
      search
    ]
  );

  return useQuery({
    queryKey: [LEADS_FILTER_OPTIONS_QUERY_KEY, params],
    queryFn: () =>
      instance
        .get<LeadsFilterOptions>(
          `${QUERY}?${queryString.stringify(params, { arrayFormat: "brackets" })}`
        )
        .then(({ data }) => data),
    // Ticking a tag changes the key; keep the list on screen while counts reload.
    placeholderData: keepPreviousData,
    enabled
  });
};
